// frontend/src/testing/annuity-whale-enhanced-frontend.tsx
import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Users, 
  CheckCircle, 
  AlertTriangle, 
  Calendar, 
  DollarSign, 
  BarChart, 
  PieChart, 
  ChevronDown, 
  Menu, 
  Search,
  Bell,
  User,
  FileText,
  TrendingUp,
  Globe,
  MessageSquare,
  Settings,
  Plus,
  ChevronRight,
  Clock,
  Star,
  ThumbsUp,
  ThumbsDown,
  Search as SearchIcon,
  Share2,
  Mail,
  Phone
} from 'lucide-react';

const AnnuityWhaleEnhancedFrontend = () => {
  // State management
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showLeadDetailsModal, setShowLeadDetailsModal] = useState(false);
  const [showCountyModal, setShowCountyModal] = useState(false);
  const [currentCounty, setCurrentCounty] = useState(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  
  const [userData, setUserData] = useState({
    name: 'Jake Mossing',
    email: 'jake@annuitywhale.com',
    avatar: null,
    accountType: 'Pro',
    claimedCounties: ['Maricopa County, AZ', 'Eaton County, MI'],
    adBudget: {
      monthly: '$7,000',
      used: '$3,250',
      remaining: '$3,750',
      projectedLeads: 40,
      costPerLead: '$175',
      activeAds: 12
    },
    leads: [
      { id: 1, name: 'John D.', county: 'Maricopa County, AZ', value: '$250,000', status: 'New', date: '04/10/2025', phone: '(480) 555-1234', email: 'john.d@example.com', notes: 'Interested in fixed indexed annuities', source: 'Google Search', quality: 'High', searchTerm: 'best fixed annuity rates' },
      { id: 2, name: 'Sarah M.', county: 'Maricopa County, AZ', value: '$175,000', status: 'Contacted', date: '04/09/2025', phone: '(480) 555-5678', email: 'sarah.m@example.com', notes: 'Looking for retirement income options', source: 'Bing Search', quality: 'Medium', searchTerm: 'retirement income planning' },
      { id: 3, name: 'Robert T.', county: 'Maricopa County, AZ', value: '$400,000', status: 'Meeting Scheduled', date: '04/08/2025', phone: '(480) 555-8765', email: 'robert.t@example.com', notes: 'Wants to discuss annuity options in person', source: 'Google Search', quality: 'High', searchTerm: 'annuity advisor near me' },
      { id: 4, name: 'Patricia L.', county: 'Eaton County, MI', value: '$225,000', status: 'New', date: '04/07/2025', phone: '(517) 555-4321', email: 'patricia.l@example.com', notes: 'Recently retired, interested in safe investment options', source: 'Bing Search', quality: 'High', searchTerm: 'safest retirement investments' },
      { id: 5, name: 'Michael W.', county: 'Eaton County, MI', value: '$310,000', status: 'Meeting Scheduled', date: '04/06/2025', phone: '(517) 555-7890', email: 'michael.w@example.com', notes: 'Has questions about annuity rates and terms', source: 'Google Search', quality: 'Medium', searchTerm: 'fixed annuity rates comparison' },
      { id: 6, name: 'Jennifer K.', county: 'Eaton County, MI', value: '$185,000', status: 'Contacted', date: '04/05/2025', phone: '(517) 555-3456', email: 'jennifer.k@example.com', notes: 'Looking to rollover 401k into something safer', source: 'Bing Search', quality: 'Medium', searchTerm: '401k rollover options' },
    ],
    notifications: [
      { id: 1, message: 'New lead assigned: John D.', time: '2 hours ago', read: false, type: 'lead' },
      { id: 2, message: 'Meeting reminder: Sarah M. at 2:00 PM', time: '5 hours ago', read: false, type: 'meeting' },
      { id: 3, message: 'Lead status update: Robert T. is now "Meeting Scheduled"', time: '1 day ago', read: true, type: 'update' },
      { id: 4, message: 'Your ad in Eaton County is performing well - 22% CTR', time: '2 days ago', read: true, type: 'performance' },
      { id: 5, message: 'Budget alert: Approaching monthly ad spend limit', time: '3 days ago', read: true, type: 'budget' },
    ],
    stats: {
      leadsThisMonth: 15,
      leadsConverted: 4,
      averageValue: '$224,167',
      conversionRate: '26.7%',
      totalContractValue: '$1,850,000',
      roi: '21.4x',
    },
    searchTerms: [
      { term: 'best fixed annuity rates', impressions: 246, clicks: 42, leads: 3, county: 'All Counties' },
      { term: 'retirement income planning', impressions: 189, clicks: 35, leads: 2, county: 'All Counties' },
      { term: 'annuity advisor near me', impressions: 167, clicks: 28, leads: 4, county: 'All Counties' },
      { term: 'safest retirement investments', impressions: 154, clicks: 26, leads: 2, county: 'Eaton County, MI' },
      { term: 'fixed annuity rates comparison', impressions: 132, clicks: 22, leads: 1, county: 'Maricopa County, AZ' },
      { term: '401k rollover options', impressions: 129, clicks: 24, leads: 3, county: 'All Counties' },
    ]
  });
  
  const [availableCounties, setAvailableCounties] = useState([
    { id: 1, name: 'Franklin County, OH', status: 'available', leadsPerMonth: '3-5', price: '$67', population: '1.32M', avgIncome: '$64,500', seniorPopulation: '182,500', leadPotential: 'Medium', competitorCount: 3 },
    { id: 2, name: 'Denver County, CO', status: 'available', leadsPerMonth: '4-7', price: '$67', population: '706K', avgIncome: '$72,600', seniorPopulation: '98,200', leadPotential: 'High', competitorCount: 5 },
    { id: 3, name: 'Wake County, NC', status: 'available', leadsPerMonth: '2-4', price: '$67', population: '1.15M', avgIncome: '$78,400', seniorPopulation: '142,300', leadPotential: 'Medium', competitorCount: 2 },
    { id: 4, name: 'Maricopa County, AZ', status: 'claimed', leadsPerMonth: '5-9', price: '$67', population: '4.49M', avgIncome: '$68,700', seniorPopulation: '748,000', leadPotential: 'Very High', competitorCount: 0 },
    { id: 5, name: 'King County, WA', status: 'available', leadsPerMonth: '6-8', price: '$67', population: '2.27M', avgIncome: '$102,900', seniorPopulation: '342,400', leadPotential: 'High', competitorCount: 4 },
    { id: 6, name: 'Harris County, TX', status: 'available', leadsPerMonth: '7-10', price: '$67', population: '4.71M', avgIncome: '$69,200', seniorPopulation: '615,300', leadPotential: 'Very High', competitorCount: 6 },
    { id: 7, name: 'Orange County, CA', status: 'available', leadsPerMonth: '8-12', price: '$67', population: '3.17M', avgIncome: '$94,500', seniorPopulation: '482,000', leadPotential: 'Very High', competitorCount: 8 },
    { id: 8, name: 'Eaton County, MI', status: 'claimed', leadsPerMonth: '2-4', price: '$67', population: '110K', avgIncome: '$58,800', seniorPopulation: '22,500', leadPotential: 'Medium', competitorCount: 0 },
  ]);

  // Performance data for charts
  const [performanceData, setPerformanceData] = useState({
    leadConversion: [
      { name: 'Leads Received', value: 15, percentage: 100 },
      { name: 'Initial Contact Made', value: 12, percentage: 80 },
      { name: 'Meetings Scheduled', value: 8, percentage: 53 },
      { name: 'Contracts Signed', value: 4, percentage: 27 }
    ],
    monthlyLeads: [
      { month: 'Jan', leads: 5 },
      { month: 'Feb', leads: 7 },
      { month: 'Mar', leads: 10 },
      { month: 'Apr', leads: 15 },
      { month: 'May', leads: 0 },
      { month: 'Jun', leads: 0 },
    ],
    leadSources: [
      { source: 'Google Search', percentage: 65 },
      { source: 'Bing Search', percentage: 25 },
      { source: 'Direct Traffic', percentage: 7 },
      { source: 'Other', percentage: 3 },
    ],
    adPerformance: [
      { county: 'Maricopa County, AZ', impressions: 1245, clicks: 187, leads: 9, costPerLead: '$167' },
      { county: 'Eaton County, MI', impressions: 780, clicks: 124, leads: 6, costPerLead: '$183' },
    ]
  });

  // Filter states for the leads tab
  const [leadFilters, setLeadFilters] = useState({
    status: 'all',
    county: 'all',
    dateRange: 'all',
    quality: 'all',
    search: ''
  });

  // Handle tab selection
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setMobileMenuOpen(false);
  };

  // Get color for status badges
  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'Contacted': return 'bg-yellow-100 text-yellow-700';
      case 'Meeting Scheduled': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-purple-100 text-purple-700';
      case 'Contract Signed': return 'bg-green-700 text-white';
      case 'Contract Declined': return 'bg-red-100 text-red-700';
      case 'No Response': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'lead': return <User className="text-blue-500" size={16} />;
      case 'meeting': return <Calendar className="text-green-500" size={16} />;
      case 'update': return <CheckCircle className="text-yellow-500" size={16} />;
      case 'performance': return <TrendingUp className="text-purple-500" size={16} />;
      case 'budget': return <DollarSign className="text-red-500" size={16} />;
      default: return <Bell className="text-gray-500" size={16} />;
    }
  };

  // Filter leads based on leadFilters
  const filteredLeads = userData.leads.filter(lead => {
    // Filter by status
    if (leadFilters.status !== 'all' && lead.status !== leadFilters.status) {
      return false;
    }
    
    // Filter by county
    if (leadFilters.county !== 'all' && lead.county !== leadFilters.county) {
      return false;
    }
    
    // Filter by quality
    if (leadFilters.quality !== 'all' && lead.quality !== leadFilters.quality) {
      return false;
    }
    
    // Filter by search term
    if (leadFilters.search && 
        !lead.name.toLowerCase().includes(leadFilters.search.toLowerCase()) && 
        !lead.county.toLowerCase().includes(leadFilters.search.toLowerCase()) &&
        !lead.email.toLowerCase().includes(leadFilters.search.toLowerCase())) {
      return false;
    }
    
    // For simplicity, we'll skip date range filtering in this example
    return true;
  });

  // Handle claiming a new county
  const handleClaimCounty = (countyId) => {
    const county = availableCounties.find(county => county.id === countyId);
    if (county) {
      // Update the county status
      const updatedCounties = availableCounties.map(c => 
        c.id === countyId ? { ...c, status: 'claimed' } : c
      );
      setAvailableCounties(updatedCounties);
      
      // Add to user's claimed counties
      setUserData({
        ...userData,
        claimedCounties: [...userData.claimedCounties, county.name]
      });
      
      setShowCountyModal(false);
      
      // Show success message (in a real app, this would be a modal or toast)
      alert(`Successfully claimed ${county.name}! You'll start receiving leads in this territory.`);
    }
  };

  // Handle lead status update
  const handleLeadStatusUpdate = (leadId, newStatus, contractAmount = null) => {
    const updatedLeads = userData.leads.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    );
    
    setUserData({
      ...userData,
      leads: updatedLeads
    });
    
    setShowReportModal(false);
    
    // Show success message
    alert(`Lead status updated successfully to ${newStatus}`);
  };

  // Handle search term test
  const handleSearchTermTest = (term) => {
    alert(`Testing search term: "${term}"\n\nThis would open a new tab showing search results for this term. In a real implementation, this would help you verify your ad visibility without affecting your ad metrics.`);
  };

  // Handle budget update
  const handleBudgetUpdate = (newBudget) => {
    setUserData({
      ...userData,
      adBudget: {
        ...userData.adBudget,
        monthly: newBudget,
        projectedLeads: Math.floor(parseInt(newBudget.replace('$', '').replace(',', '')) / parseInt(userData.adBudget.costPerLead.replace('$', ''))),
      }
    });
    
    setShowBudgetModal(false);
    
    // Show success message
    alert(`Budget updated successfully to ${newBudget} per month.`);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <header className="bg-blue-900 text-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
                <svg viewBox="0 0 200 200" className="w-8 h-8">
                  <circle cx="100" cy="100" r="100" fill="#1e40af"/>
                  <path d="M120,40C150,40 160,15 185,15C210,15 225,25 225,40C225,55 215,65 200,65L120,65C105,65 95,55 95,40C95,25 105,15 120,15Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                  <path d="M95,40C85,40 75,30 70,15C90,20 95,30 95,40Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                  <circle cx="185" cy="35" r="5" fill="#000000" transform="scale(0.4) translate(40, 80)"/>
                  <path d="M185,15L185,0L190,0L190,15Z" fill="#60a5fa" transform="scale(0.4) translate(40, 80)"/>
                </svg>
              </div>
              <div className="text-xl font-bold">Annuity Whale</div>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={() => setShowCountyModal(true)}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md text-sm font-medium"
            >
              Claim New County
            </button>
            <div className="relative">
              <Bell 
                className="h-5 w-5 cursor-pointer text-blue-200 hover:text-white" 
                onClick={() => setShowNotifications(!showNotifications)}
              />
              {userData.notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-xs">
                  {userData.notifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
            <div className="relative">
              <div 
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
              >
                <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
                  {userData.avatar ? (
                    <img src={userData.avatar} alt="Avatar" className="w-8 h-8 rounded-full" />
                  ) : (
                    <span className="text-sm font-medium">{userData.name.charAt(0)}</span>
                  )}
                </div>
                <span className="text-sm font-medium">Hello, {userData.name}</span>
                <ChevronDown size={16} />
              </div>
            </div>
          </div>
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-blue-800 text-white">
          <div className="container mx-auto px-4 py-2">
            <div className="flex flex-col space-y-3">
              <div className="flex items-center space-x-2 py-2 border-b border-blue-700">
                <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
                  {userData.avatar ? (
                    <img src={userData.avatar} alt="Avatar" className="w-8 h-8 rounded-full" />
                  ) : (
                    <span className="text-sm font-medium">{userData.name.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium">{userData.name}</div>
                  <div className="text-xs text-blue-300">{userData.email}</div>
                </div>
              </div>
              <button 
                onClick={() => handleTabChange('dashboard')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Dashboard
              </button>
              <button 
                onClick={() => handleTabChange('leads')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                My Leads
              </button>
              <button 
                onClick={() => handleTabChange('territories')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Territory Map
              </button>
              <button 
                onClick={() => handleTabChange('marketplace')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                County Marketplace
              </button>
              <button 
                onClick={() => handleTabChange('performance')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Performance Reporting
              </button>
              <button 
                onClick={() => handleTabChange('campaigns')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Ad Campaigns
              </button>
              <button 
                onClick={() => setShowCountyModal(true)}
                className="bg-green-600 hover:bg-green-700 w-full py-2 rounded-md text-sm font-medium mt-3"
              >
                Claim New County
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white shadow-sm hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => handleTabChange('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'leads'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Leads
            </button>
            <button
              onClick={() => handleTabChange('territories')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'territories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Territory Map
            </button>
            <button
              onClick={() => handleTabChange('marketplace')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'marketplace'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              County Marketplace
            </button>
            <button
              onClick={() => handleTabChange('performance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Performance Reporting
            </button>
            <button
              onClick={() => handleTabChange('campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'campaigns'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Ad Campaigns
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Dashboard */}
        {selectedTab === 'dashboard' && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Advisor Dashboard</h1>
              <div className="mt-3 md:mt-0 flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Apr 11, 2025</span>
                </div>
                <div className="bg-blue-100 text-blue-800 py-1 px-3 rounded-full text-xs font-semibold flex items-center">
                  <Star className="h-3 w-3 mr-1" /> {userData.accountType} Account
                </div>
                <button 
                  onClick={() => setShowBudgetModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  <DollarSign className="-ml-1 mr-2 h-4 w-4 text-gray-500" />
                  Set Budget
                </button>
              </div>
            </div>
            
            {/* Ad Budget Overview */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-lg shadow-lg mb-8 text-white">
              <div className="px-6 py-4 border-b border-blue-600">
                <h2 className="text-lg font-medium">Ad Budget Overview</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div className="col-span-2">
                    <div className="flex flex-col h-full justify-center">
                      <h3 className="text-xl font-semibold">{userData.adBudget.monthly} Monthly Budget</h3>
                      <p className="text-sm text-blue-100 mt-1">Optimized for Targeted Annuity Leads</p>
                      <div className="mt-4 h-3 bg-blue-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-400" 
                          style={{ width: `${parseInt(userData.adBudget.used.replace(', ', '').replace(',', '')) / parseInt(userData.adBudget.monthly.replace(', ', '').replace(',', '')) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1 text-sm">
                        <span>Used: {userData.adBudget.used}</span>
                        <span>Remaining: {userData.adBudget.remaining}</span>
                      </div>
                      <button 
                        onClick={() => setShowBudgetModal(true)}
                        className="mt-4 bg-white text-blue-800 hover:bg-blue-50 px-4 py-2 rounded font-medium text-sm inline-flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Adjust Budget
                      </button>
                    </div>
                  </div>
                  
                  <div className="col-span-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-800 bg-opacity-50 p-4 rounded-lg">
                        <div className="text-sm text-blue-200">Projected Leads</div>
                        <div className="text-3xl font-bold mt-1">{userData.adBudget.projectedLeads}</div>
                        <div className="text-xs text-blue-300 mt-1">This Month</div>
                      </div>
                      <div className="bg-blue-800 bg-opacity-50 p-4 rounded-lg">
                        <div className="text-sm text-blue-200">Average Cost Per Lead</div>
                        <div className="text-3xl font-bold mt-1">{userData.adBudget.costPerLead}</div>
                        <div className="text-xs text-blue-300 mt-1">Based on Current Performance</div>
                      </div>
                      <div className="bg-blue-800 bg-opacity-50 p-4 rounded-lg">
                        <div className="text-sm text-blue-200">Active Ads</div>
                        <div className="text-3xl font-bold mt-1">{userData.adBudget.activeAds}</div>
                        <div className="text-xs text-blue-300 mt-1">Across All Counties</div>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-blue-100 flex items-start">
                      <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span>
                        Your annuity lead program is optimized for quality over quantity. 
                        Remember: it only takes one high-value "white whale" lead to make your investment worthwhile.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                    <Users size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Leads This Month</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsThisMonth}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100 text-green-600">
                    <CheckCircle size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Converted Leads</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsConverted}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                    <DollarSign size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg. Lead Value</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.averageValue}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <TrendingUp size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.conversionRate}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Territories */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Your Claimed Territories</h2>
                <button 
                  onClick={() => setShowCountyModal(true)}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded inline-flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" /> Claim County
                </button>
              </div>
              <div className="p-6">
                {userData.claimedCounties.length > 0 ? (
                  <div className="space-y-4">
                    {userData.claimedCounties.map((county, index) => (
                      <div key={index} className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center mb-3 md:mb-0">
                          <MapPin className="text-blue-600 mr-2" size={20} />
                          <span className="font-medium">{county}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-sm bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded">
                            View Stats
                          </button>
                          <button 
                            onClick={() => handleTabChange('leads')}
                            className="text-sm bg-blue-600 text-white px-3 py-1 rounded"
                          >
                            Manage Leads
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">You haven't claimed any territories yet.</p>
                    <button 
                      onClick={() => handleTabChange('marketplace')}
                      className="mt-2 bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      Claim Your First County
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Top Search Terms */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Top Performing Search Terms</h2>
                <div>
                  <button className="text-sm text-blue-600 hover:text-blue-800">
                    View All
                  </button>
                </div>
              </div>
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Search Term
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Impressions
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clicks
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Leads
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Test
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userData.searchTerms.slice(0, 4).map((term, index) => (
                      <tr key={index}>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{term.term}</div>
                          <div className="text-xs text-gray-500">{term.county}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{term.impressions}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{term.clicks}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{term.leads}</div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <button 
                            onClick={() => handleSearchTermTest(term.term)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Test Search
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Leads */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Recent Lead Activity</h2>
                <div className="flex items-center">
                  <select className="text-sm border-gray-300 rounded-md">
                    <option>Last 7 Days</option>
                    <option>Last 30 Days</option>
                    <option>All Time</option>
                  </select>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {userData.leads.slice(0, 3).map((lead) => (
                  <div key={lead.id} className="p-6 flex items-center">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {lead.name} - {lead.county}
                      </p>
                      <p className="text-sm text-gray-500">
                        Annuity Value: {lead.value} · {lead.date}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Search Term: "{lead.searchTerm}"
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-4 border-t border-gray-200">
                <button 
                  onClick={() => handleTabChange('leads')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all leads →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leads */}
        {selectedTab === 'leads' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Export Leads
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {/* Search and filters */}
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search leads..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full md:w-64"
                      value={leadFilters.search}
                      onChange={(e) => setLeadFilters({...leadFilters, search: e.target.value})}
                    />
                  </div>
                  <div className="flex space-x-2 overflow-x-auto">
                    <select 
                      className="border border-gray-300 rounded-md px-3 py-2"
                      value={leadFilters.status}
                      onChange={(e) => setLeadFilters({...leadFilters, status: e.target.value})}
                    >
                      <option value="all">All Statuses</option>
                      <option value="New">New</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Meeting Scheduled">Meeting Scheduled</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <select 
                      className="border border-gray-300 rounded-md px-3 py-2"
                      value={leadFilters.county}
                      onChange={(e) => setLeadFilters({...leadFilters, county: e.target.value})}
                    >
                      <option value="all">All Counties</option>
                      {userData.claimedCounties.map((county, index) => (
                        <option key={index} value={county}>{county}</option>
                      ))}
                    </select>
                    <select 
                      className="border border-gray-300 rounded-md px-3 py-2"
                      value={leadFilters.quality}
                      onChange={(e) => setLeadFilters({...leadFilters, quality: e.target.value})}
                    >
                      <option value="all">All Quality</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                    <select 
                      className="border border-gray-300 rounded-md px-3 py-2"
                      value={leadFilters.dateRange}
                      onChange={(e) => setLeadFilters({...leadFilters, dateRange: e.target.value})}
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Data Table */}
              <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Lead
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              County
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Value
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quality
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredLeads.map((lead) => (
                            <tr key={lead.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                    <User size={16} className="text-gray-500" />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                                    <div className="text-xs text-gray-500">{lead.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{lead.county}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{lead.value}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{lead.date}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                                  {lead.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {lead.quality === 'High' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                      <ThumbsUp className="h-3 w-3 mr-1" />
                                      High
                                    </span>
                                  )}
                                  {lead.quality === 'Medium' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                      Medium
                                    </span>
                                  )}
                                  {lead.quality === 'Low' && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                      <ThumbsDown className="h-3 w-3 mr-1" />
                                      Low
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex space-x-2 justify-end">
                                  <button 
                                    onClick={() => {
                                      setCurrentLead(lead);
                                      setShowLeadDetailsModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    View
                                  </button>
                                  <button 
                                    onClick={() => {
                                      setCurrentLead(lead);
                                      setShowReportModal(true);
                                    }}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Report Outcome
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pagination */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                    Previous
                  </button>
                  <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredLeads.length}</span> of <span className="font-medium">{filteredLeads.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                        <span className="sr-only">Previous</span>
                        <ChevronDown className="h-5 w-5 transform rotate-90" />
                      </button>
                      <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                        1
                      </button>
                      <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                        <span className="sr-only">Next</span>
                        <ChevronDown className="h-5 w-5 transform -rotate-90" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Territory Map Placeholder */}
        {selectedTab === 'territories' && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Territory Map</h1>
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Your Claimed Counties</h2>
              </div>
              <div className="p-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <div className="text-gray-500">
                    <Globe className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="text-lg font-medium mb-2 mt-4">Interactive Map Coming Soon</p>
                    <p className="max-w-md mx-auto">The interactive territory map will allow you to view available counties and your claimed territories.</p>
                  </div>
                </div>
                
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userData.claimedCounties.map((county, index) => (
                    <div key={index} className="bg-blue-50 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-blue-900">{county}</h3>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Active Leads:</span>
                          <span className="font-medium text-gray-900">
                            {userData.leads.filter(lead => lead.county === county).length}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Conversion Rate:</span>
                          <span className="font-medium text-gray-900">
                            {Math.round(userData.leads.filter(lead => lead.county === county && lead.status === 'Contract Signed').length / 
                            Math.max(1, userData.leads.filter(lead => lead.county === county).length) * 100)}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Ad Performance:</span>
                          <span className="font-medium text-gray-900">
                            {performanceData.adPerformance.find(item => item.county === county)?.clicks || 0} clicks
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-2">
                        <button className="text-sm bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded flex-1">
                          View Stats
                        </button>
                        <button 
                          onClick={() => {
                            setLeadFilters({...leadFilters, county: county});
                            handleTabChange('leads');
                          }}
                          className="text-sm bg-blue-600 text-white px-3 py-1 rounded flex-1"
                        >
                          View Leads
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* County Marketplace */}
        {selectedTab === 'marketplace' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">County Marketplace</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="flex space-x-2">
                  <input 
                    type="text" 
                    placeholder="Search counties..."
                    className="border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnnuityWhaleEnhancedFrontend;